import { useRateFieldError } from "../../hooks/useRateFieldError";
import { FC, useRef, useState } from "react";

import { Theme } from "@mui/material/styles";
import { TicketingFormCategory } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";
import { ConfirmRateChangeDialog } from "../ConfirmRateChangeDialog/ConfirmRateChangeDialog";
import { formatRatePpmAsPercent, sumTaxRatePpm } from "../TaxesField/helpers";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { getLabel, isMin, isMinOrZero, isRequired } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useTicketingTaxes } from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useTicketingTaxes";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface AmountFieldProps {
  disabled?: boolean;
  labelOverride?: string;
  tooltipText?: string;
}

export const AmountField: FC<AmountFieldProps> = ({ disabled = false, labelOverride, tooltipText }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { organizationCurrency } = useCurrentUserContext();
  const { formCategory, hasExistingPayments, isAddOn, isSweepstakes, canModifyRenewalAmount, unlockRenewalAmount } =
    useRateContext();
  const { control } = useFormContext<StepTicketsFormData>();

  const fieldName = useRateFieldName("amount");
  const error = useRateFieldError("amount");

  const isAuction = formCategory === TicketingFormCategory.Auction;
  const isShop = formCategory === TicketingFormCategory.Shop;
  const isCustom = formCategory === TicketingFormCategory.Custom;
  const isMembership = formCategory === TicketingFormCategory.MembershipV2;

  const isPayWhatYouCan = useWatch({ control, name: useRateFieldName("isPayWhatYouCan") });
  const allowAutomaticRenewal = useWatch({ control, name: useRateFieldName("allowAutomaticRenewal") });
  const currentAmount = useWatch({ control, name: fieldName });
  const taxIds = useWatch({ control, name: useRateFieldName("taxIds") });

  const { taxes } = useTicketingTaxes();
  const selectedTaxRatePpm = sumTaxRatePpm(taxIds, new Map(taxes.map((tax) => [tax.id, tax.ratePpm])));
  const isTaxableAmount = Boolean(isPayWhatYouCan) || (currentAmount ?? 0) > 0;
  const taxHeader =
    selectedTaxRatePpm > 0 && isTaxableAmount ? (
      <DashboardTypography variant="caption" vibe="text-neutral-quiet">
        {t("dashboard", "ticketing.taxes.amountHeader", {
          percent: formatRatePpmAsPercent(selectedTaxRatePpm, locale),
        })}
      </DashboardTypography>
    ) : undefined;

  const isNonZeroAutoRenewalSubscription = isMembership && Boolean(allowAutomaticRenewal) && (currentAmount ?? 0) > 0;

  const requiresPriceChangeConfirmation = isMembership && hasExistingPayments;

  const inputRef = useRef<HTMLInputElement>(null);
  const canModifyPrice = !requiresPriceChangeConfirmation || canModifyRenewalAmount;
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleClick = () => {
    if (requiresPriceChangeConfirmation && !isConfirmOpen && !canModifyPrice) {
      setIsConfirmOpen(true);
    }
  };

  const handleConfirm = () => {
    if (inputRef.current instanceof HTMLInputElement) {
      inputRef.current.select();
      unlockRenewalAmount();
    }
    setIsConfirmOpen(false);
  };

  const label =
    labelOverride ??
    (isAuction ? t("dashboard", "ticketing.auctionStartingPriceTitle") : t("dashboard", "ticketing.rateAmount"));
  const requiredMessage =
    isSweepstakes && !isAddOn
      ? t("dashboard", "sweepstakes.rates.amountRequired")
      : t(
          "dashboard",
          isAddOn
            ? "ticketing.rateAmountAddOnIsRequired"
            : isCustom || isShop
              ? "ticketing.rateAmountItemIsRequired"
              : "ticketing.rateAmountIsRequired"
        );
  const minRule = isAuction
    ? isMin(100, t("dashboard", "ticketing.rateAmountMinimum"))
    : isNonZeroAutoRenewalSubscription
      ? isMin(100, t("dashboard", "form.rate.update.disallowedSubscriptionAmountUpdate"))
      : isMinOrZero(100, t("dashboard", "ticketing.rateAmountMinimum"));

  if (isPayWhatYouCan) {
    return (
      <DashboardTextField
        label={getLabel(true, label)}
        headerRight={taxHeader}
        value={t("dashboard", "ticketing.payWhatYouCanAmountPlaceholder") || ""}
        disabled
        sx={(theme: Theme) => ({
          opacity: 1,
          backgroundColor: theme.palette.surface.neutral.quiet,
          borderColor: theme.palette.border.neutral.quiet,
          "&:hover": { borderColor: theme.palette.border.neutral.quiet },
          "& .MuiInputBase-input.Mui-disabled": {
            color: theme.palette.text.neutral.quiet,
            WebkitTextFillColor: theme.palette.text.neutral.quiet,
          },
        })}
        data-test="rate-amount-input-pwyc"
      />
    );
  }

  return (
    <>
      <DashboardAmountInput
        control={control}
        data-test="rate-amount-input"
        locale={locale}
        currency={organizationCurrency}
        onClick={handleClick}
        label={label}
        tooltipText={tooltipText}
        headerRight={taxHeader}
        inputProps={{ ref: inputRef }}
        disabled={disabled}
        name={fieldName}
        rules={[isRequired(requiredMessage), minRule].filter(Boolean)}
        errorMessage={error?.message}
      />
      <ConfirmRateChangeDialog
        open={isConfirmOpen}
        variant="price"
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirm}
      />
    </>
  );
};
